/**
 * Datas e fuso (architecture.md §5.5).
 *
 * O fuso de exibição é FIXO em America/Sao_Paulo — nunca o do navegador. Um
 * pastor viajando não pode abrir o calendário e ver tudo deslocado, e um
 * horário de culto não muda porque quem olha está em outro país.
 *
 * Sem biblioteca de data: `Intl` resolve tudo que a v1 precisa e não
 * introduz dependência fora do tech-stack. O FullCalendar deve ser
 * configurado com `timeZone: TIME_ZONE`.
 */

export const TIME_ZONE = 'America/Sao_Paulo';

export const LOCALE = 'pt-BR';

type Parts = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
};

const partsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function zonedParts(value: string | Date): Parts {
  const date = typeof value === 'string' ? new Date(value) : value;
  const result: Record<string, string> = {};
  for (const part of partsFormatter.formatToParts(date)) {
    if (part.type !== 'literal') result[part.type] = part.value;
  }
  return {
    year: result.year ?? '',
    month: result.month ?? '',
    day: result.day ?? '',
    // Intl pode devolver "24" para meia-noite em hourCycle h23/h24.
    hour: result.hour === '24' ? '00' : (result.hour ?? ''),
    minute: result.minute ?? '',
  };
}

/** `05/08/2026` */
export function formatDate(value: string | Date): string {
  const { day, month, year } = zonedParts(value);
  return `${day}/${month}/${year}`;
}

/** `5 de agosto de 2026` — para cabeçalhos e telas de leitura. */
export function formatDateLong(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** `19:30` */
export function formatTime(value: string | Date): string {
  const { hour, minute } = zonedParts(value);
  return `${hour}:${minute}`;
}

/** `05/08/2026 às 19:30` */
export function formatDateTime(value: string | Date): string {
  return `${formatDate(value)} às ${formatTime(value)}`;
}

/**
 * Intervalo do projeto. Projeto multi-dia (retiro, congresso) é caso normal,
 * não exceção — por isso as duas formas.
 *
 * Mesmo dia:    `05/08/2026, 19:30 às 22:00`
 * Dias distintos: `05/08/2026 19:30 até 07/08/2026 12:00`
 */
export function formatDateTimeRange(start: string | Date, end: string | Date): string {
  if (isSameZonedDay(start, end)) {
    return `${formatDate(start)}, ${formatTime(start)} às ${formatTime(end)}`;
  }
  return `${formatDate(start)} ${formatTime(start)} até ${formatDate(end)} ${formatTime(end)}`;
}

export function isSameZonedDay(a: string | Date, b: string | Date): boolean {
  return toDateInputValue(a) === toDateInputValue(b);
}

/**
 * `28/02` — dia e mês, sem ano.
 *
 * O ano sai de propósito: a forma existe para dizer a JANELA de uma consulta (a
 * vizinhança da data, decisão 38), que cobre poucas semanas em torno de uma
 * data já escrita por extenso logo acima. Repetir o ano duas vezes na mesma
 * coluna estreita não informa nada e força uma quebra de linha.
 *
 * Devolve as duas partes juntas e nada mais — quem escreve o travessão entre
 * elas é `messages.review.neighborhoodRange`, porque a pontuação de um intervalo
 * é escolha de idioma, não de data (RNF-U04).
 */
export function formatDayMonth(value: string | Date): string {
  const { day, month } = zonedParts(value);
  return `${day}/${month}`;
}

/**
 * `2026-09` — chave de agrupamento por mês. NUNCA exibida.
 *
 * Carrega o ano de propósito: uma chave só de mês ordenaria dezembro/2026
 * depois de janeiro/2027, e a lista de projetos atravessa a virada do ano toda
 * vez que alguém marca um retiro de carnaval.
 */
export function monthKey(value: string | Date): string {
  const { year, month } = zonedParts(value);
  return `${year}-${month}`;
}

/**
 * `set.` — rótulo de eixo do gráfico de volume da Início.
 *
 * Existe aqui, e não na feature que o desenha, pela regra 9: TODA formatação de
 * data passa por este arquivo, no fuso da igreja. Uma coluna de gráfico rotulada
 * pelo mês do navegador seria o mesmo bug de sempre, num lugar onde ninguém
 * pensaria em procurá-lo.
 *
 * O ano fica de fora de propósito — a série cabe em dez colunas e o `title` de
 * cada uma diz o mês por extenso, com ano. Repetir "2026" dez vezes num eixo de
 * 375px custa a legibilidade que o rótulo existe para dar.
 */
export function formatMonthShort(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, month: 'short' }).format(date);
}

/** `setembro de 2026` — cabeçalho de grupo de mês. */
export function formatMonthLabel(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * `Agosto 2026` · `Agosto – Setembro 2026` — o TÍTULO da tela do calendário
 * (spec-calendar-ux.md §2.1, decisão 80).
 *
 * ⚠️ **Não é o título que o FullCalendar escreve**, e a diferença é o motivo de
 * esta função existir. Aquele muda de anatomia com a vista ("agosto de 2026",
 * "11 – 17 de agosto de 2026", "14 de agosto de 2026"), e um cabeçalho de tela
 * que troca de formato a cada botão obriga a reler. Aqui a forma é sempre a
 * mesma — mês (ou meses) e ano —, e a vista de Dia não repete a data de
 * propósito: ela já está no cabeçalho da coluna e por extenso na lista ao lado.
 *
 * O ano aparece uma vez só quando os dois meses estão nele; quando a semana
 * atravessa a virada, cada um leva o seu.
 *
 * Sem `capitalize` do lado de quem chama: em `pt-BR` só a primeira palavra sobe,
 * e a utilitária do Tailwind subiria "Setembro" também. Quem sobe a inicial é
 * `first-letter:uppercase`.
 *
 * @param start        primeiro instante do período visível
 * @param endExclusive primeiro instante DEPOIS dele — é o que o `datesSet` do
 *                     FullCalendar entrega, e usá-lo cru faria uma semana que
 *                     termina no sábado ser rotulada com o mês do domingo
 *                     seguinte.
 */
export function formatMonthYear(start: string | Date, endExclusive: string | Date): string {
  const first = zonedParts(start);
  const lastInstant = new Date(
    (typeof endExclusive === 'string' ? new Date(endExclusive) : endExclusive).getTime() - 1,
  );
  const last = zonedParts(lastInstant);

  const monthOf = (value: string | Date) =>
    new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, month: 'long' }).format(
      typeof value === 'string' ? new Date(value) : value,
    );

  if (first.year === last.year && first.month === last.month) {
    return `${monthOf(start)} ${first.year}`;
  }
  if (first.year === last.year) {
    return `${monthOf(start)} – ${monthOf(lastInstant)} ${first.year}`;
  }
  return `${monthOf(start)} ${first.year} – ${monthOf(lastInstant)} ${last.year}`;
}

/**
 * As três partes do bloco de data da linha do tempo: `{ '05', 'set', 'sex' }`.
 *
 * Separadas em vez de uma frase pronta porque cada uma tem corpo e peso
 * diferentes no bloco — o dia é o que se lê varrendo a coluna, o resto é apoio.
 * O dia da semana entra porque quem planeja evento de igreja pensa em sábado e
 * domingo antes de pensar em número.
 */
export function formatDayParts(value: string | Date): {
  day: string;
  month: string;
  weekday: string;
} {
  const date = typeof value === 'string' ? new Date(value) : value;
  const parts = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: 'short',
    weekday: 'short',
  }).formatToParts(date);

  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  // `pt-BR` devolve "set." e "sex." com ponto; o ponto vira ruído numa coluna
  // estreita de três linhas.
  const strip = (value: string) => value.replace(/\.$/, '');

  return {
    day: pick('day'),
    month: strip(pick('month')),
    weekday: strip(pick('weekday')),
  };
}

/** `2026-08-05` — valor de `<input type="date">`, já no fuso da igreja. */
export function toDateInputValue(value: string | Date): string {
  const { year, month, day } = zonedParts(value);
  return `${year}-${month}-${day}`;
}

/** `19:30` — valor de `<input type="time">`. */
export function toTimeInputValue(value: string | Date): string {
  return formatTime(value);
}

/**
 * Anda dias no formato do `<input type="date">`, sem passar por `Date` local.
 *
 * `new Date('2026-08-14')` é meia-noite UTC — somar um dia e reformatar no fuso
 * da igreja devolveria o MESMO dia (21h de -03:00). Contando em UTC dos dois
 * lados, o calendário é o civil e a virada de mês e de ano vem de graça.
 */
export function addDaysToDateInput(date: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) throw new Error(`Data em formato inesperado: "${date}"`);

  const shifted = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days),
  );
  return shifted.toISOString().slice(0, 10);
}

/**
 * Junta os dois campos do formulário em um timestamptz.
 *
 * O usuário digita hora de parede da igreja; o banco guarda instante absoluto.
 * O offset é lido do próprio `Intl` no instante em questão em vez de fixado em
 * -03:00 — se o Brasil voltar a ter horário de verão, isto continua correto.
 *
 * @param date `yyyy-mm-dd`
 * @param time `HH:mm`
 * @returns ISO 8601 com offset, ex. `2026-08-05T19:30:00-03:00`
 */
export function fromDateAndTimeInputs(date: string, time: string): string {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dateMatch || !timeMatch) {
    throw new Error(`Data ou hora em formato inesperado: "${date}" "${time}"`);
  }

  const approximateUtc = Date.UTC(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3]),
    Number(timeMatch[1]),
    Number(timeMatch[2]),
  );

  return `${date}T${time}:00${zoneOffsetAt(new Date(approximateUtc))}`;
}

/** `-03:00` */
function zoneOffsetAt(instant: Date): string {
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    timeZoneName: 'longOffset',
  })
    .formatToParts(instant)
    .find((part) => part.type === 'timeZoneName')?.value;

  const offset = name?.replace('GMT', '') ?? '';
  return offset === '' ? '+00:00' : offset;
}

/**
 * Dias de calendário decorridos no fuso da igreja — é o que a fila de análise
 * chama de "há quantos dias aguarda" (US-18). Enviado ontem às 23h conta como
 * 1 dia, não como 0.
 */
export function daysSince(value: string | Date, now: Date = new Date()): number {
  const start = Date.parse(`${toDateInputValue(value)}T00:00:00Z`);
  const end = Date.parse(`${toDateInputValue(now)}T00:00:00Z`);
  return Math.round((end - start) / 86_400_000);
}

/** Primeiro e último instante do mês visível, para consultar o calendário. */
export function monthRange(reference: Date = new Date()): { from: string; to: string } {
  const { year, month } = zonedParts(reference);
  const from = `${year}-${month}-01`;
  const nextMonth = Number(month) === 12 ? '01' : String(Number(month) + 1).padStart(2, '0');
  const nextYear = Number(month) === 12 ? String(Number(year) + 1) : year;
  return {
    from: fromDateAndTimeInputs(from, '00:00'),
    to: fromDateAndTimeInputs(`${nextYear}-${nextMonth}-01`, '00:00'),
  };
}
